import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { locale } from "../locale";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { userPlaceholder } from "../user/user";
import { centeredBox } from "../van/centered-box";
import { button, div, h1, p } from "../van/van-tags";

export class DeleteAccountPage implements Route {
	async #deleteUser(): Promise<void> {
		const req = this.#ctx.api.delete(`/users/${this.#reactive.user.Id}`);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			state.signOut();
			this.#ctx.router.jump("/sign-in");
		} else if (res.status === 403) {
			const err = await res.text();
			this.#reactive.helpText = err;
		}
	}

	async refresh(_: Match): Promise<void> {
		this.#reactive.user = await state.getActiveUser();
	}

	#ctx: RouteContext;
	#reactive = reactive({
		helpText: "",
		user: userPlaceholder,
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1(
				{ class: "title" },
				locale.string({
					enGB: "Confirm account deletion",
					deDE: "Kontolöschung bestätigen",
					frFR: "Confirmer la suppression du compte",
					svSE: "Bekräfta radering av konto",
				}),
			),
			div(
				{ class: "field" },
				p(
					locale.string({
						enGB: "By clicking confirm you agree to cancelling all active subscriptions owned by this account without a refund and the immediate deletion of all data associated with the account. Note that some data, such as data regarding financial transactions, may be kept in accordance with currently applicable law.",
						deDE: "Durch Bestätigen erklären Sie sich mit der kündigungsfreien Beendigung aller mit diesem Konto verknüpften Abonnements ohne Rückerstattung sowie der sofortigen Löschung aller Kontodaten einverstanden. Bitte beachten Sie, dass bestimmte Daten, wie z. B. Finanztransaktionen, gemäß geltendem Recht aufbewahrt werden können.",
						frFR: "En cliquant sur confirmer, vous acceptez l'annulation sans remboursement de tous les abonnements actifs liés à ce compte et la suppression immédiate de toutes les données associées. Veuillez noter que certaines données, telles que celles relatives aux transactions financières, peuvent être conservées conformément à la loi en vigueur.",
						svSE: "Genom att bekräfta godkänner du att alla aktiva prenumerationer kopplade till kontot avslutas utan återbetalning, samt att all data kopplad till kontot raderas omedelbart. Notera att viss data, såsom uppgifter om finansiella transaktioner, kan komma att lagras i enlighet med gällande lagstiftning.",
					}),
				),
			),
			div(
				{
					class: "field is-grouped is-multiline",
				},
				div(
					{ class: "control" },
					button(
						{
							class: "button is-danger",
							onclick: () => this.#deleteUser(),
						},
						locale.string({
							enGB: "Confirm",
							deDE: "Bestätigen",
							frFR: "Confirmer",
							svSE: "Bekräfta",
						}),
					),
				),
				div(
					{
						class: "control",
						onclick: () => {
							this.#ctx.router.jump("/account");
						},
					},
					button(
						{ class: "button" },
						locale.string({
							enGB: "Back",
							deDE: "Zurück",
							frFR: "Retour",
							svSE: "Tillbaka",
						}),
					),
				),
			),
			p({ class: "help is-danger" }, () => this.#reactive.helpText),
		);
	}
}
